import { BizEntityFunctionTypes, BizEntityTypeCategory, IBizEntity, IBizEntityType } from "@optima-stride-ai/core";
import { IPolymorphicControlMeta } from "@optima-stride-ai/optima-components";
import { OptimaDefaultModalService } from "./modal.service";


export enum ModalDialogs {
  MeasureFilterDefinition,
  AddNewField,
  TeamChooser,
  DeliverableChooser,
  AddRoleField,
  AddTeamField,
  MeasureChooser,
  Message,
  KPIChooser,
  BizRules,
  StrategyChooser,
  RelationshipConfigurator,
  CheckInChooser,
  ConfirmationDialogue,
  RenameDialogue,
  LibraryChooser,
  GenericExplorer
}


export enum TitleSizes {
  large = "1.5rem", larger = "2rem", largest = "3rem"
}

export interface IModalFooterButton {
  id: string;
  label: string;
  color: string;
  closesModal: boolean;
  formValidate?: boolean;
  getData?: boolean; //If this is true, extract data from the modal form.
  click: Function;
  isEnabled?: Function;
}

export enum ModalDialogSizes {
  SM = "sm",
  LG = "lg",
  XL = "xl",
  FULL_SCREEN = "fs"
}

export interface IModalConfig {
  dialog: ModalDialogs;
  title: string;
  backdrop: boolean | 'static';
  alignment: 'center' | undefined;
  id: string;
  size: ModalDialogSizes;
  titleSize: TitleSizes;
  model: { 'in': any, 'out': any };
  footerButtons: IModalFooterButton[];
  level?: number;
  selectedEntityDisplay?: { key: string }[];
}

export interface IModalExplorerConfig extends IModalConfig {
  bizEntityFunctionTypeFilter: BizEntityFunctionTypes;
  entityTypeIds?: string[];
  appId?: string; // For any app specific explorer, we can use this to filter out the explorer options and also pass it down to the explorer component for it to decide what data to show.
}

export function GetDefaultConfig(): IModalConfig {
  return {
    dialog: ModalDialogs.MeasureFilterDefinition,
    title: 'Modal Form',
    backdrop: 'static',
    alignment: 'center',
    id: new Date().getTime().toString(),
    size: ModalDialogSizes.LG,
    titleSize: TitleSizes.larger,
    model: { 'in': null, 'out': null },
    footerButtons: [],
    level: 1
  }
}

export function GetDefaultExplorerConfig(): IModalExplorerConfig {
  return {
    dialog: ModalDialogs.MeasureFilterDefinition,
    title: 'Modal Form',
    backdrop: 'static',
    alignment: 'center',
    id: new Date().getTime().toString(),
    size: ModalDialogSizes.LG,
    titleSize: TitleSizes.larger,
    model: { 'in': null, 'out': null },
    footerButtons: [],
    level: 1,
    bizEntityFunctionTypeFilter: BizEntityFunctionTypes.Structural
  }
}


export interface IExplorerProperties {
  name: string;
  explorer: Explorers;
  modalChooser?: ModalDialogs;
}

export enum Explorers {
  Strategy = 1,
  Measure,
  KPI,
  // Team,
  CheckIn,
  Library
}


export const explorerProperties: IExplorerProperties[] = [
  {
    name: "Strategy",
    explorer: Explorers.Strategy,
    modalChooser: ModalDialogs.StrategyChooser
  },
  {
    name: "Measure",
    explorer: Explorers.Measure,
    modalChooser: ModalDialogs.MeasureChooser
  },
    {
    name: "Library",
    explorer: Explorers.Library,
    modalChooser: ModalDialogs.LibraryChooser
  },
  {
    name: "KPI",
    explorer: Explorers.KPI,
    modalChooser: ModalDialogs.KPIChooser
  },
  // {
  //     name: "Team",
  //     explorer: Explorers.Team,
  //     modalChooser: ModalDialogs.TeamChooser
  // },
  {
    name: "Check-in",
    explorer: Explorers.CheckIn,
    modalChooser: ModalDialogs.CheckInChooser
  }
];

export function getExplorerPropertiesBasedOnBizEntityType(bizEntityType: IBizEntityType): IExplorerProperties | undefined {
  switch (bizEntityType.category) {
    case BizEntityTypeCategory.BenzaitenKPI:
      return explorerProperties.find(x => x.explorer === Explorers.KPI);
    case BizEntityTypeCategory.BenzaitenMeasure:
      return explorerProperties.find(x => x.explorer === Explorers.Measure);
    case BizEntityTypeCategory.HayagrivaFileMarkdown:
    case BizEntityTypeCategory.HayagrivaFileCSV:
    case BizEntityTypeCategory.HayagrivaFileSpreadsheet:
    case BizEntityTypeCategory.HayagrivaFileDoc:
    case BizEntityTypeCategory.HayagrivaFilePresentation:
    case BizEntityTypeCategory.HayagrivaFileDrawing:
      return explorerProperties.find(x => x.explorer === Explorers.Library);
    // case BizEntityTypeCategory.Team:
    // case BizEntityTypeCategory.User:
    //     return explorerProperties.find(x => x.explorer === Explorers.Team);

    case BizEntityTypeCategory.Transactional:
    case BizEntityTypeCategory.Compositional:
      return explorerProperties.find(x => x.explorer === Explorers.CheckIn);
    default:
      return explorerProperties.find(x => x.explorer === Explorers.Strategy);
  }
}

export function launchExplorerDialog(c: IPolymorphicControlMeta, optimaDefaultModalService: OptimaDefaultModalService, callbackOnSelect?: Function, callBackOnSubmitEnableCheck?: Function) {
  let modalConfig = GetDefaultExplorerConfig()
  modalConfig.title = 'Select ' + c.label;
  // modalConfig.dialog = ModalDialogs.StrategyChooser;
  modalConfig.dialog = explorerProperties.find(x => x.explorer.toString() === c.explorerSelected?.toString())?.modalChooser || ModalDialogs.StrategyChooser;
  modalConfig.size = ModalDialogSizes.XL;
  modalConfig.footerButtons.push({
    id: 'biz-entity-select',
    label: 'Confirm Selection',
    color: 'primary',
    click: (data: IBizEntity) => {
      c.value = data.id;
      c.selectedBizEntity = data;
      if (callbackOnSelect) {
        callbackOnSelect(data);
      }
    },
    isEnabled: (data: IBizEntity) => {
      if (c.isSelectedBizEntityValid !== undefined) {
        if (callBackOnSubmitEnableCheck) {
          return callBackOnSubmitEnableCheck(data);
        }
        return c.isSelectedBizEntityValid(data.id);
      }
      if (modalConfig.dialog === ModalDialogs.StrategyChooser) {
        if (data.id) {
          return true;
        }
        return false;
      }
      return data.type.functionType === BizEntityFunctionTypes.Operational;
    },
    getData: true,
    formValidate: false,
    closesModal: true
  });
  modalConfig.bizEntityFunctionTypeFilter = BizEntityFunctionTypes.Operational;
  optimaDefaultModalService.show(modalConfig);
}